import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 본사 자리배치도 (V392) — 도면 위의 자리와 거기 앉은 사람.
 *
 * <p>자리는 좌표(그림의 0~1 비율)와 사람 id 만 들고 온다. 이름·직급·부서는 화면이 이미 들고 있는
 * 명부({@code personnel}·{@code personnelAssignments})에서 잇는다 — 서버가 베껴 보내면 개명·퇴사가
 * 자리배치도에만 늦게 온다.
 *
 * <p>고칠 수 있는가({@code canEdit})는 서버가 답한다 — 최고관리자 또는 인사총무팀. 표시용이고,
 * 실제 판정은 쓰기 요청마다 서버가 다시 한다({@code PlacementAuthority.requireSeatEditor}).
 */
export type HqSeat = {
  id: string;
  /** '3층' · '5층'. {@code SeatMapView.FLOORS} 의 글자와 같다. */
  floor: string;
  /** 도면에 적힌 팀 이름. 그 구역의 이름이지 앉은 사람의 소속이 아니다. */
  zone: string | null;
  seatNo: number;
  posX: number;
  posY: number;
  personnelId: string | null;
};

export type HqSeatMap = { canEdit: boolean; seats: HqSeat[] };

export function useHqSeats(enabled = true) {
  return useQuery({
    queryKey: queryKeys.hqSeats,
    queryFn: () => apiFetch<HqSeatMap>('/v1/hr/seats'),
    enabled,
  });
}

/**
 * 앉히기·비우기·자리 더하기·옮기기·지우기.
 */
export function useHqSeatWrite() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.hqSeats });
  };

  const assign = useMutation({
    mutationFn: ({ seatId, personnelId }: { seatId: string; personnelId: string | null }) =>
      apiFetch<HqSeat>(`/v1/hr/seats/${seatId}/occupant`, { method: 'PUT', body: { personnelId } }),
    onSuccess: invalidate,
  });

  const create = useMutation({
    mutationFn: (body: { floor: string; posX: number; posY: number }) =>
      apiFetch<HqSeat>('/v1/hr/seats', { method: 'POST', body }),
    onSuccess: invalidate,
  });

  const move = useMutation({
    mutationFn: ({ seatId, posX, posY }: { seatId: string; posX: number; posY: number }) =>
      apiFetch<HqSeat>(`/v1/hr/seats/${seatId}/position`, { method: 'PUT', body: { posX, posY } }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (seatId: string) => apiFetch<void>(`/v1/hr/seats/${seatId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  return { assign, create, move, remove };
}
